import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";

import { ConsentProvider } from "@/components/consent/ConsentProvider";
import { CookieBanner } from "@/components/consent/CookieBanner";
import { ScrollToTopButton } from "@/components/ScrollToTopButton";
import { NotFoundPage } from "@/components/NotFoundPage";
import { Toaster } from "@/components/ui/sonner";
import { initWebVitalsReporter } from "@/lib/rum/report-web-vitals";
import appCss from "../styles.css?url";
// Schrift-Preloads: Die Pfade müssen ZEICHENGLEICH mit den src-URLs der
// @font-face-Regeln in src/styles.css sein (dort /fonts/…). Ein Bundler-Import
// (?url) erzeugt eine andere URL — der Browser lädt dann zweimal und die
// Schrift schaltet verzögert um.
const FUNNEL_DISPLAY_800 = "/fonts/funnel-display-latin-800-normal.woff2";
const MERRIWEATHER_400 = "/fonts/merriweather-latin-400-normal.woff2";


function ErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="heading-h1">Die Seite konnte nicht geladen werden</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Bitte versuche es erneut oder kehre zur Startseite zurück.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90"
          >
            Erneut versuchen
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
          >
            Zur Startseite
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#004b51" },
      { property: "og:site_name", content: "creategy" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      // Leaf routes provide their own title, description, og:title, og:image.
    ],
    links: [
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: FUNNEL_DISPLAY_800,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: MERRIWEATHER_400,
        crossOrigin: "anonymous",
      },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon-192.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96.png" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],

    scripts: [
      {
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',functionality_storage:'granted',security_storage:'granted',wait_for_update:500});gtag('set','ads_data_redaction',true);gtag('set','url_passthrough',false);",
      },
      // Der GTM-Container wird nicht mehr im <head> geladen, sondern erst
      // nach erteiltem Consent (siehe ConsentProvider) – spart ~490 KiB
      // Payload beim Erstaufruf.

      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "creategy",
          legalName: "creategy GmbH",
          url: "https://creategy.de",
          logo: "https://creategy.de/favicon-192.png",
          description:
            "Spezialagentur für politische Kommunikation: Wahlkampf, Verbandskommunikation und Krisenkommunikation.",
          areaServed: "DE",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Danziger Straße 6",
            postalCode: "10435",
            addressLocality: "Berlin",
            addressRegion: "Berlin",
            addressCountry: "DE",
          },
          location: [
            {
              "@type": "Place",
              name: "Regionalbüro Rhein-Main",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Hainstädter Straße 29",
                postalCode: "63512",
                addressLocality: "Hainburg",
                addressCountry: "DE",
              },
            },
            {
              "@type": "Place",
              name: "Regionalbüro Baden-Württemberg",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Heinrich-Bettinger Str. 14",
                postalCode: "78333",
                addressLocality: "Stockach",
                addressCountry: "DE",
              },
            },
          ],
          vatID: "DE356700125",
          taxID: "37/254/53871",
          sameAs: [
            "https://www.linkedin.com/company/creategy",
            "https://www.instagram.com/creategy",
          ],
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: "+49-30-27693422",
              contactType: "customer service",
              email: "hello@creategy.de",
              areaServed: "DE",
              availableLanguage: ["de"],
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "creategy",
          url: "https://creategy.de",
          inLanguage: "de-DE",
          publisher: { "@type": "Organization", name: "creategy GmbH" },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="de">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-WNPVG62"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
        >
          Zum Inhalt springen
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    initWebVitalsReporter();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ConsentProvider>
        <Outlet />
        <CookieBanner />
        <ScrollToTopButton />
        <Toaster position="top-center" richColors closeButton />
      </ConsentProvider>
    </QueryClientProvider>
  );
}
